import { useState, useEffect } from "react";
import "./feedback.css";
import {
  Typography,
  Card,
  Select,
  Input,
  Tag,
  Space,
  Button,
  Row,
  Col,
  DatePicker,
  Tooltip,
  Avatar,
  Table,
  Modal,
  Pagination,
  Popconfirm,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  UserOutlined,
  EyeOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  FlagFilled,
  ReloadOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import {
  fetchFeedbackAPI,
  flagTableRowApi,
  getAllFiltersApi,
} from "../../services/feedback.service";
import FeedbackDetailDialog from "./FeedbackDetailDialog.component";
import { useSearchParams } from "react-router-dom";
import { logout } from "../../store/slices/profile.slice";
import { formatDateTime } from "../../utils/formatDateTime";
import DataTablePagination from "../../components/common/dataTablePagination";

const { Title, Text } = Typography;
const { Option } = Select;

const FeedbackInsightsDashboard = () => {
  const [feedbackData, setFeedbackData] = useState([]);
  const [totalFeedbackCount, setTotalFeedbackCount] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState();
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { dateRangeValue } = useAppSelector((state) => state.dateRange);
  const { RangePicker } = DatePicker;
  const [showUserId, setShowUserId] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Initialize state from URL parameters
  const [searchFeedback, setSearchFeedback] = useState(searchParams.get("search") || undefined);
  const [status, setStatus] = useState(searchParams.get("status") || "unresolved");
  const [page, setPage] = useState(parseInt(searchParams.get("page")) || 1);
  const [limit, setLimit] = useState(parseInt(searchParams.get("limit")) || 10);
  const [category, setCategory] = useState(searchParams.get("category") ? searchParams.get("category").split(",") : undefined);
  
  // For date range: check URL first, then fall back to Redux state
  const urlStartDate = searchParams.get("startDate");
  const urlEndDate = searchParams.get("endDate");
  const [dates, setDates] = useState(() => {
    // If both dates exist and are not empty strings, use them
    if (urlStartDate !== null && urlEndDate !== null && urlStartDate !== "" && urlEndDate !== "") {
      return [urlStartDate, urlEndDate];
    }
    // If dates were explicitly cleared (empty strings), keep them cleared
    if (urlStartDate === "" && urlEndDate === "") {
      return [];
    }
    // Otherwise (no date params), use Redux state
    return [
      dayjs(dateRangeValue.start_date).format("YYYY-MM-DD"),
      dayjs(dateRangeValue.end_date).format("YYYY-MM-DD"),
    ];
  });
  const [dateRange, setDateRange] = useState(() => {
    // If both dates exist and are not empty strings, use them
    if (urlStartDate !== null && urlEndDate !== null && urlStartDate !== "" && urlEndDate !== "") {
      return [dayjs(urlStartDate), dayjs(urlEndDate)];
    }
    // If dates were explicitly cleared (empty strings), keep them cleared
    if (urlStartDate === "" && urlEndDate === "") {
      return null;
    }
    // Otherwise (no date params), use Redux state
    return [
      dayjs(dateRangeValue.start_date),
      dayjs(dateRangeValue.end_date),
    ];
  });
  
  const [refreshTrigger, setRefreshTrigger] = useState(false);

  const uniqueCategories = Array.from(
    new Set(feedbackData.flatMap((item) => item?.categories))
  );

  const seeFullDetail = (record) => {
    console.log("record -> ", record);
    setSelectedRecord(record);
    setDetailVisible(true);
  };

  const handleClearFilter = () => {
    setDates([]);
    setDateRange(null);
    setCategory();
    setSearchFeedback();
    setStatus();
    setPage(1);
    // Clear URL parameters
    setSearchParams({}, { replace: true });
  };

  const fetchAllFilters = () => {
    dispatch(
      getAllFiltersApi({
        finalCallback: () => {},
        successCallback: (response) => {
          setFilters(response);
        },
        failureCallback: (errors) => {},
      })
    );
  };

  const handleFlagTableRow = (record, resolved) => {
    const data = {
      evaluation_id: record?._id,
      resolved_id: record?.resolved_id ?? null,
      resolved: resolved,
    };

    dispatch(
      flagTableRowApi({
        data: data,
        finalCallback: () => {},
        successCallback: () => {
          fetchFeedback();
        },
        failureCallback: (errors) => {},
      })
    );
  };

  useEffect(() => {
    fetchAllFilters();
  }, []);

  useEffect(() => {
    fetchFeedback();
  }, [page, limit, dates, category, searchFeedback, status, refreshTrigger]);

  // Update URL parameters when filter state changes
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (searchFeedback) params.set("search", searchFeedback);
    if (status) params.set("status", status);
    if (page !== 1) params.set("page", page.toString());
    if (limit !== 10) params.set("limit", limit.toString());
    if (category && category.length > 0) params.set("category", category.join(","));
    
    // Handle date range: set params if dates exist, set empty if explicitly cleared
    if (dates && dates.length === 2) {
      params.set("startDate", dates[0]);
      params.set("endDate", dates[1]);
    } else if (dates && dates.length === 0) {
      // Dates were explicitly cleared - track this with empty values
      params.set("startDate", "");
      params.set("endDate", "");
    }
    
    const newSearch = params.toString();
    const currentSearch = searchParams.toString();
    
    if (newSearch !== currentSearch) {
      setSearchParams(params, { replace: true });
    }
  }, [searchFeedback, status, page, limit, category, dates, setSearchParams, searchParams]);

  const fetchFeedback = () => {
    setLoading(true);
    dispatch(
      fetchFeedbackAPI({
        params: {
          page: page,
          limit: limit,
          start_date: dates ? dates[0] : null,
          end_date: dates ? dates[1] : null,
          search_query: searchFeedback,
          category: category,
          resolved:
            status === "resolved"
              ? true
              : status === "unresolved"
              ? false
              : undefined,
        },
        finalCallback: () => {
          setLoading(false);
        },
        successCallback: (responses) => {
          console.log("API Response:", responses);
          if (responses?.data) {
            setFeedbackData(responses.data);
            setTotalFeedbackCount(responses.meta.total_count);
          }
        },
        failureCallback: (errors) => {
          setLoading(false);
        },
      })
    );
  };

  const columns = [
    {
      title: "Customer Name",
      dataIndex: "username",
      key: "userName",
      width: 150,
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Tooltip title={text} overlayStyle={{ maxWidth: "50vw" }}>
            <Text
              strong
              style={{
                fontSize: "14px",
                display: "block",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {text}
            </Text>
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Customer Query",
      dataIndex: "message",
      width: 250,
      key: "message",
      render: (text) => (
        <Tooltip title={text} overlayStyle={{ maxWidth: "50vw" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div
              style={{
                maxWidth: "250px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {text}
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: "AI Response",
      dataIndex: "reply",
      width: 250,
      key: "reply",
      render: (text) => (
        <Tooltip title={text} overlayStyle={{ maxWidth: "50vw" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div
              style={{
                maxWidth: "250px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {text}
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Feedback",
      dataIndex: "remark",
      width: 300,
      key: "remark",
      render: (text) => (
        <Tooltip title={text} overlayStyle={{ maxWidth: "50vw" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div
              style={{
                maxWidth: "300px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {text}
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Details",
      key: "details",
      width: 160, // enough space for text
      render: (_, record) => (
        <Button
          color="primary"
          variant="filled"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => seeFullDetail(record)}
        >
          View Details
        </Button>
      ),
    },
    {
      title: "Categories",
      dataIndex: "categories",
      width: 200,
      key: "categories",
      render: (categories) => {
        const displayedCategories = (categories || []).slice(0, 2);
        const hiddenCount = (categories || []).length - 2;
        const hasMore = hiddenCount > 0;

        return (
          <Space wrap>
            {displayedCategories.map((category) => (
              <Tag
                // color="blue"
                style={{
                  textAlign: "center",
                }}
              >
                {category}
              </Tag>
            ))}
            {hasMore && (
              <Tag
                style={{
                  borderRadius: "4px",
                  backgroundColor: "#f0f0f0",
                  border: "none",
                  color: "#595959",
                }}
              >
                +{hiddenCount} more
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (text) => (
        <Tooltip title={formatDateTime({ date: text, withTime: true })}>
          <span style={{ whiteSpace: "nowrap" }}>
            {formatDateTime({ date: text })}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      dataIndex: "resolved",
      width: 100,
      render: (resolved, record) =>
        resolved ? (
          <Popconfirm
            placement="topLeft"
            title="Are you sure you want to unresolve it?"
            onConfirm={() => handleFlagTableRow(record, false)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Unresolve this feedback">
              <Button
                icon={<CloseOutlined />}
                size="small"
                variant="filled"
                color="danger"
              >
                Unresolve
              </Button>
            </Tooltip>
          </Popconfirm>
        ) : (
          <Popconfirm
            placement="topLeft"
            title="Are you sure you want to resolve it?"
            onConfirm={() => handleFlagTableRow(record, true)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Resolve this feedback">
              <Button
                icon={<CheckOutlined />}
                size="small"
                variant="filled"
                color="success"
                style={{
                  backgroundColor: "#dcfce7",
                  color: "#16a34a",
                }}
              >
                Resolve
              </Button>
            </Tooltip>
          </Popconfirm>
        ),
    },
  ];

  const handleSearchFeedback = (e) => {
    setPage(1);
    const value = e.target.value;
    setSearchFeedback(value === "" ? undefined : value);
  };

  const handleDateChange = (values) => {
    setPage(1);
    setDateRange(values);
    if (values) {
      const stringDates = values.map((date) => date.format("YYYY-MM-DD"));
      setDates(stringDates);
      console.log("Selected Dates:", stringDates);
    } else {
      setDates([]);
    }
  };

  const handleCategorySelect = (selected) => {
    setPage(1);
    setCategory(selected && selected.length > 0 ? selected : undefined);
  };

  return (
    <div
      className="feedback-dashboard"
      style={{
        backgroundColor: "#f5f5f5",
        height: "90vh",
      }}
      id="feedback-dashboard"
    >
      <Card
        className="dashboard-header"
        style={{
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            borderBottom: "1px solid #f0f0f0",
            marginBottom: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: "8px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Title
              level={4}
              style={{
                color: "#333",
                fontWeight: 700,
                margin: 0,
              }}
            >
              FeedBack Report
            </Title>
            <Tooltip title={`${totalFeedbackCount} Feedback Logs found for selected filters`}>
              <Tag
                color="blue"
                style={{
                  margin: 0,
                  borderRadius: 999,
                  padding: "2px 10px",
                  fontWeight: 600,
                }}
              >
                {totalFeedbackCount} Feedback Logs Found
              </Tag>
            </Tooltip>
          </div>
          <Space>
            {/* <Tooltip title="Refresh Data" placement="top">
              <Button
                type="text"
                icon={<ReloadOutlined />}
                size="medium"
                style={{ color: "#1890ff" }}
                onClick={() => setRefreshTrigger((prev) => !prev)}
              >
                Refresh
              </Button>
            </Tooltip> */}
            <Tooltip title="Clear Filters" placement="top">
              <Button
                type="text"
                // variant="outlined"
                // color="danger"
                icon={<CloseCircleOutlined />}
                size="medium"
                style={{ color: "#ff4d4f" }}
                onClick={handleClearFilter}
              >
                Clear Filters
              </Button>
            </Tooltip>
          </Space>
        </div>

        <Row gutter={[16, 16]} align="top" style={{ width: "100%" }}>
          <Col xs={24} sm={12} md={6} lg={6} xl={6}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Search
            </Text>
            <Input
              size="middle"
              placeholder="Search feedback..."
              prefix={<SearchOutlined style={{ color: "#1890ff" }} />}
              value={searchFeedback}
              onChange={handleSearchFeedback}
              style={{
                borderRadius: "8px",
                backgroundColor: "#f9f9f9",
                border: "1px solid #e8e8e8",
                boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
              }}
              allowClear
            />
          </Col>

          {/* Date Range now takes 2x width */}
          <Col xs={24} sm={24} md={12} lg={8} xl={8}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Date Range
            </Text>
            <RangePicker
              value={dateRange}
              disabledDate={(d) => d && d.valueOf() > Date.now()}
              onChange={handleDateChange}
              style={{
                width: "100%",
                borderRadius: "8px",
                backgroundColor: "#f9f9f9",
                border: "1px solid #e8e8e8",
              }}
              placeholder={["Start date", "End date"]}
              allowClear
              format="YYYY-MM-DD"
              suffixIcon={<CalendarOutlined style={{ color: "#1890ff" }} />}
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={6} xl={6}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Categories
            </Text>
            <Select
              mode="multiple"
              style={{ width: "100%", backgroundColor: "#f9f9f9" }}
              placeholder="Select categories"
              size="middle"
              suffixIcon={<FilterOutlined style={{ color: "#1890ff" }} />}
              onChange={handleCategorySelect}
              value={category}
              allowClear
              maxTagCount={1}
              showArrow
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
            >
              {filters?.categories?.map((category) => (
                <Option key={category} value={category}>
                  {category}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6} lg={4} xl={4}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Status
            </Text>
            <Select
              style={{ width: "100%", backgroundColor: "#f9f9f9" }}
              placeholder="Status"
              size="middle"
              suffixIcon={<FilterOutlined style={{ color: "#1890ff" }} />}
              onChange={(value) => {
                setPage(1);
                setStatus(value);
              }}
              value={status}
              allowClear
              showArrow
              options={[
                { value: "resolved", label: "Resolved" },
                { value: "unresolved", label: "Unresolved" },
              ]}
            />
          </Col>
        </Row>
      </Card>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          width: "100%",
          background: "white",
          borderRadius: "8px",
          overflow: "hidden",
          paddingBottom: "16px"
        }}
        >
        <div
          style={{
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
          }}
        >
          <Table
            className="feedback-table"
            dataSource={feedbackData}
            columns={columns}
            rowKey={(item) => item._id}
            pagination={false}
            bordered
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              width: "100%",
            }}
            rowClassName={(record) =>
              record.evaluation === "like"
                ? "table-row-positive"
                : record.evaluation === "dislike"
                ? "table-row-negative"
                : ""
            }
            footer={false}
          />
        </div>

        {detailVisible && selectedRecord && (
          <FeedbackDetailDialog
            data={selectedRecord}
            open={detailVisible}
            setOpen={setDetailVisible}
          />
        )}
        <div>
          <DataTablePagination
            currentPage={page}
            pageSize={limit}
            totalDocuments={totalFeedbackCount}
            setPageNumber={setPage}
            setPageSize={setLimit}
            showTotal={(total) => `Total ${total} Feedback Logs`}
          />
        </div>
      </div>
      <br />
      <br />
    </div>
  );
};

export default FeedbackInsightsDashboard;
